// client/src/writer/hooks/useWorldBuildingManager.ts
import { useCallback, useContext } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AppContext } from '../contexts/AppContext';
import { apiRequest } from '../../lib/queryClient';
import { cacheManager } from '../../utils/cacheUtils';
import { useToast } from '../../hooks/use-toast';
import {
    WorldBuildingData,
    CharacterProfile,
    DetailedCharacter,
    LocationProfile,
    CustomWorldBuildingCategoryItem,
    BookMetadata,
} from '../types';

export interface WorldBuildingManagerLogic {
    worldBuildingData: WorldBuildingData;
    handleRefineCharactersWithAI: (text: string) => void;
    handleRefineLocationsWithAI: (text: string) => void;
    handleGenerateCustomCategoryItemsWithAI: (categoryIndex: number, userRequest: string) => void;
    handleRefineCustomCategoryItemsWithAI: (categoryIndex: number, newText: string) => void;
    handleRegenerateAllWorldBuildingData: (userRevisionPrompt: string) => void;
    handleStoryTitleIdeasChange: (ideas: string[]) => void;
    handleSelectedStoryTitleChange: (title: string) => void;
    handleCharacterChange: (index: number, field: keyof CharacterProfile, value: string) => void;
    handleAddCharacter: () => void;
    handleDeleteCharacter: (index: number) => void;
    handleDeleteAllCharacters: () => void;
    handleLocationChange: (index: number, field: keyof LocationProfile, value: string) => void;
    handleAddLocation: () => void;
    handleDeleteLocation: (index: number) => void;
    handleDeleteAllLocations: () => void;
    handleAddCustomCategory: () => void;
    handleCustomCategoryNameChange: (catIndex: number, newName: string) => void;
    handleDeleteCustomCategory: (catIndex: number) => void;
    handleDeleteAllCustomCategories: () => void;
    handleAddCustomItem: (catIndex: number) => void;
    handleCustomItemChange: (catIndex: number, itemIndex: number, field: keyof CustomWorldBuildingCategoryItem, value: string) => void;
    handleDeleteCustomItem: (catIndex: number, itemIndex: number) => void;
    
    // Extended character functions for CharactersView
    handleCharacterNameChange: (index: number, value: string) => void;
    handleCharacterDescriptionChange: (index: number, value: string) => void;
    handleCharacterRoleChange: (index: number, value: string) => void;
    handleCharacterBackstoryChange: (index: number, value: string) => void;
    handleCharacterGoalsChange: (index: number, value: string) => void;
    handleCharacterConflictsChange: (index: number, value: string) => void;
    handleCharacterPersonalityChange: (index: number, value: string) => void;
    handleCharacterAppearanceChange: (index: number, value: string) => void;
    handleCharacterRelationshipsChange: (index: number, value: string) => void;
    handleCharacterDevelopmentChange: (index: number, value: string) => void;
    handleCharacterNotesChange: (index: number, value: string) => void;
    handleRefineCharacters: () => void;
    handleExpandCharacters: () => void;
    isRefineCharactersLoading: boolean;
    isExpandCharactersLoading: boolean;
}

interface UseWorldBuildingManagerProps {
    worldBuildingData: WorldBuildingData;
    onWorldBuildingDataChange: (field: keyof WorldBuildingData | 'all', value: any) => void;
    outlineIdea: string;
    bookMetadata: BookMetadata;
    coreConcepts: { logLine: string; storyStartConcept: string; storyEndConcept: string };
}

export const useWorldBuildingManager = ({
    worldBuildingData,
    onWorldBuildingDataChange,
    outlineIdea,
    bookMetadata,
    coreConcepts,
}: UseWorldBuildingManagerProps): WorldBuildingManagerLogic => {
    
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const appContext = useContext(AppContext);
    
   const setLoadingState =
       appContext?.setLoadingState ?? (() => {});
   
       const apiKey = appContext?.apiKey;
   const modalStates = appContext?.modalStates;
   const openApiKeyModal = modalStates?.openApiKeyModal;

    const aiMutation = useMutation({
        mutationFn: async ({ task, payload }: { task: string; payload: any }) => {
            const response = await apiRequest(`/api/llm/${task}`, 'POST', payload);
            return response.json();
        },
        onSuccess: async (data, variables) => {
            const { task, payload } = variables;
            let message = '';

            switch (task) {
                case 'world-building-characters': {
                    const chars = data.data.map((p: any) => ({
                        id: p.id || `char-${Date.now()}`,
                        name: p.name || '',
                        briefDescription: p.briefDescription || '',
                        role: p.role || '',
                        description: p.description || p.briefDescription || '',
                        backstory: p.backstory || '',
                        goals: p.goals || '',
                        conflicts: p.conflicts || '',
                        personality: p.personality || '',
                        appearance: p.appearance || '',
                        relationships: p.relationships || '',
                        developmentArc: p.developmentArc || '',
                        notes: p.notes || ''
                    }));
                    onWorldBuildingDataChange('characters', chars);
                    message = `${chars.length} characters refined.`;
                    break;
                }
                case 'world-building-characters-expand': {
                    const chars = data.data.map((p: any) => ({
                        id: p.id || `char-${Date.now()}`,
                        name: p.name || '',
                        briefDescription: p.briefDescription || '',
                        role: p.role || '',
                        description: p.description || p.briefDescription || '',
                        backstory: p.backstory || '',
                        goals: p.goals || '',
                        conflicts: p.conflicts || '',
                        personality: p.personality || '',
                        appearance: p.appearance || '',
                        relationships: p.relationships || '',
                        developmentArc: p.developmentArc || '',
                        notes: p.notes || ''
                    }));
                    onWorldBuildingDataChange('characters', chars);
                    message = `${chars.length} characters expanded with more details.`;
                    break;
                }
                case 'world-building-locations': {
                    const locs = data.data.map((p: any) => ({ id: `loc-${Date.now()}`, name: p.name, briefDescription: p.briefDescription || '' }));
                    onWorldBuildingDataChange('locations', locs);
                    message = `${locs.length} locations refined.`;
                    break;
                }
                case 'world-building-custom-category-items-generate': {
                    const { categoryIndex } = payload;
                    const newItems = data.data.map((p: any) => ({ id: `item-${Date.now()}`, name: p.name, description: p.description || '' }));
                    const updated = worldBuildingData.customCategories.map((cat, i) =>
                        i === categoryIndex ? { ...cat, items: [...cat.items, ...newItems] } : cat
                    );
                    onWorldBuildingDataChange('customCategories', updated);
                    message = `${newItems.length} items added to category.`;
                    break;
                }
                case 'world-building-custom-category-items-refine': {
                    const { categoryIndex } = payload;
                    const refined = data.data.map((p: any) => ({ id: `item-${Date.now()}`, name: p.name, description: p.description || '' }));
                    const updated = worldBuildingData.customCategories.map((cat, i) =>
                        i === categoryIndex ? { ...cat, items: refined } : cat
                    );
                    onWorldBuildingDataChange('customCategories', updated);
                    message = `Category items refined.`;
                    break;
                }
                case 'world-building-regenerate-all': {
                    const raw = data.data;
                    const newData: WorldBuildingData = {
                        storyTitleIdeas: raw.storyTitleIdeas || [],
                        selectedStoryTitle: raw.selectedStoryTitle || '',
                        logLine: raw.logLine || '',
                        storyStartConcept: raw.storyStartConcept || '',
                        storyEndConcept: raw.storyEndConcept || '',
                        characters: (raw.characters || []).map((c: any, i: number) => ({
                            id: c.id || `char-${Date.now()}-${i}`,
                            name: c.name || '',
                            briefDescription: c.briefDescription || '',
                            role: c.role || '',
                            description: c.description || c.briefDescription || '',
                            backstory: c.backstory || '',
                            goals: c.goals || '',
                            conflicts: c.conflicts || '',
                            personality: c.personality || '',
                            appearance: c.appearance || '',
                            relationships: c.relationships || '',
                            developmentArc: c.developmentArc || '',
                            notes: c.notes || ''
                        })),
                        locations: (raw.locations || []).map((l: any, i: number) => ({ id: `loc-${Date.now()}-${i}`, name: l.name, briefDescription: l.briefDescription })),
                        customCategories: (raw.customCategories || []).map((c: any, ci: number) => ({
                            id: `cat-${Date.now()}-${ci}`,
                            categoryName: c.categoryName,
                            items: (c.items || []).map((it: any, ii: number) => ({ id: `item-${Date.now()}-${ci}-${ii}`, name: it.name, description: it.description })),
                        })),
                        themes: raw.themes || [],
                    };
                    onWorldBuildingDataChange('all', newData);
                    message = `All world-building data regenerated.`;
                    break;
                }
                default:
                    message = 'Operation completed.';
            }

            toast({ title: 'Success', description: message });

            // CRITICAL FIX: Fetch and update progress after world building generation
            if (appContext?.projectState?.projectId) {
                try {
                    const progressRes = await apiRequest(`/api/story-projects/${appContext.projectState.projectId}/summary`, 'GET');
                    const progressData = await progressRes.json();
                    if (progressData && progressData.progress) {

                        // Update project summary cache with fresh progress
                        queryClient.setQueryData(['story-project-summary', appContext.projectState.projectId], (oldSummary: any) => {
                            if (oldSummary) {
                                return {
                                    ...oldSummary,
                                    progress: progressData.progress,
                                    updatedAt: new Date().toISOString()
                                };
                            }
                            return progressData;
                        });

                        // Update project-summary localStorage cache
                        cacheManager.set('project-summary', appContext.projectState.projectId, {
                            ...progressData,
                        }, 5 * 60 * 1000);

                    }
                } catch (error) {
                    console.warn('Failed to fetch updated progress after world building generation:', error);
                }
            }
        },
        onError: (error: any) => {
            toast({ title: 'Error', description: error.message, variant: 'destructive' });
        },
        onSettled: () => {
            setLoadingState('parseCharacters', false);
            setLoadingState('parseLocations', false);
            setLoadingState('generateCustomCategoryItems', false);
            setLoadingState('refineCustomCategoryItems', false);
            setLoadingState('regenerateAllWorldBuilding', false);
        },
    });

    const handleRefineCharactersWithAI = useCallback(
        (text: string) => {
            if (!apiKey) { 
                toast({
                    title: "API Key Required",
                    description: "Please set your API key to use AI features.",
                    variant: "destructive"
                });
                openApiKeyModal?.('apiKey'); 
                return; 
            }
            if (!text.trim()) {
                toast({
                    title: "Input Required",
                    description: "Please enter text to refine characters.",
                    variant: "destructive"
                });
                return;
            }
            setLoadingState('parseCharacters', true);
            aiMutation.mutate({ task: 'world-building-characters', payload: { currentCharacters: worldBuildingData.characters, newText: text, outlineIdea, bookMetadata, coreConcepts } });
        },
        [apiKey, worldBuildingData.characters, outlineIdea, bookMetadata, coreConcepts, aiMutation, openApiKeyModal, setLoadingState, toast]
    );

    const handleRefineLocationsWithAI = useCallback(
        (text: string) => {
            if (!apiKey) { 
                toast({
                    title: "API Key Required",
                    description: "Please set your API key to use AI features.",
                    variant: "destructive"
                });
                openApiKeyModal?.('apiKey'); 
                return; 
            }
            if (!text.trim()) {
                toast({
                    title: "Input Required",
                    description: "Please enter text to refine locations.",
                    variant: "destructive"
                });
                return;
            }
            setLoadingState('parseLocations', true);
            aiMutation.mutate({ task: 'world-building-locations', payload: { currentLocations: worldBuildingData.locations, newText: text, outlineIdea, bookMetadata, coreConcepts } });
        },
        [apiKey, worldBuildingData.locations, outlineIdea, bookMetadata, coreConcepts, aiMutation, openApiKeyModal, setLoadingState, toast]
    );

    const handleGenerateCustomCategoryItemsWithAI = useCallback(
        (categoryIndex: number, userRequest: string) => {
            if (!apiKey) { openApiKeyModal?.('apiKey'); return; }
            setLoadingState('generateCustomCategoryItems', true);
            aiMutation.mutate({ task: 'world-building-custom-category-items-generate', payload: { categoryIndex, userRequest, outlineIdea, bookMetadata, coreConcepts } });
        },
        [apiKey, outlineIdea, bookMetadata, coreConcepts, aiMutation, openApiKeyModal, setLoadingState]
    );

    const handleRefineCustomCategoryItemsWithAI = useCallback(
        (categoryIndex: number, newText: string) => {
            if (!apiKey) { 
                toast({
                    title: "API Key Required",
                    description: "Please set your API key to use AI features.",
                    variant: "destructive"
                });
                openApiKeyModal?.('apiKey'); 
                return; 
            }
            if (!newText.trim()) {
                toast({
                    title: "Input Required",
                    description: "Please enter text to refine custom category items.",
                    variant: "destructive"
                });
                return;
            }
            setLoadingState('refineCustomCategoryItems', true);
            aiMutation.mutate({ task: 'world-building-custom-category-items-refine', payload: { categoryIndex, currentItems: worldBuildingData.customCategories[categoryIndex]?.items, newText, outlineIdea, bookMetadata, coreConcepts } });
        },
        [apiKey, worldBuildingData.customCategories, outlineIdea, bookMetadata, coreConcepts, aiMutation, openApiKeyModal, setLoadingState, toast]
    );

    const handleRegenerateAllWorldBuildingData = useCallback(
        (prompt: string) => {
            if (!apiKey) { 
                toast({
                    title: "API Key Required",
                    description: "Please set your API key to use AI features.",
                    variant: "destructive"
                });
                openApiKeyModal?.('apiKey'); 
                return; 
            }
            if (!prompt.trim()) {
                toast({
                    title: "Input Required",
                    description: "Please enter a prompt to regenerate world building data.",
                    variant: "destructive"
                });
                return;
            }
            setLoadingState('regenerateAllWorldBuilding', true);
            aiMutation.mutate({ task: 'world-building-regenerate-all', payload: { currentWorldBuildingData: worldBuildingData, userRevisionPrompt: prompt, outlineIdea, bookMetadata, coreConcepts } });
        },
        [apiKey, worldBuildingData, outlineIdea, bookMetadata, coreConcepts, aiMutation, openApiKeyModal, setLoadingState, toast]
    );

    // ----- Non-AI Handlers -----
    const handleStoryTitleIdeasChange = useCallback((ideas: string[]) => onWorldBuildingDataChange('storyTitleIdeas', ideas), [onWorldBuildingDataChange]);
    const handleSelectedStoryTitleChange = useCallback((title: string) => onWorldBuildingDataChange('selectedStoryTitle', title), [onWorldBuildingDataChange]);
    const handleCharacterChange = useCallback((idx: number, field: keyof CharacterProfile, val: string) => {
        const updated = worldBuildingData.characters.map((c, i) => (i === idx ? { ...c, [field]: val } : c));
        onWorldBuildingDataChange('characters', updated);
    }, [worldBuildingData.characters, onWorldBuildingDataChange]);
    
    const handleAddCharacter = useCallback(() => {
        const newChar: DetailedCharacter = {
            id: `char-${Date.now()}`,
            name: '',
            description: '',
            role: '',
            backstory: '',
            goals: '',
            conflicts: '',
            personality: '',
            appearance: '',
            relationships: '',
            developmentArc: '',
            notes: ''
        };
        const newArr = [...worldBuildingData.characters, newChar];
        onWorldBuildingDataChange('characters', newArr);
    }, [worldBuildingData.characters, onWorldBuildingDataChange]);
    const handleDeleteCharacter = useCallback((idx: number) => onWorldBuildingDataChange('characters', worldBuildingData.characters.filter((_, i) => i !== idx)), [worldBuildingData.characters, onWorldBuildingDataChange]);
    const handleDeleteAllCharacters = useCallback(() => { if (window.confirm('Delete all characters?')) onWorldBuildingDataChange('characters', []); }, [onWorldBuildingDataChange]);

    const handleLocationChange = useCallback((idx: number, field: keyof LocationProfile, val: string) => {
        const updated = worldBuildingData.locations.map((l, i) => (i === idx ? { ...l, [field]: val } : l));
        onWorldBuildingDataChange('locations', updated);
    }, [worldBuildingData.locations, onWorldBuildingDataChange]);
    const handleAddLocation = useCallback(() => onWorldBuildingDataChange('locations', [...worldBuildingData.locations, { id: `loc-${Date.now()}`, name: '', briefDescription: '' }]), [worldBuildingData.locations, onWorldBuildingDataChange]);
    const handleDeleteLocation = useCallback((idx: number) => onWorldBuildingDataChange('locations', worldBuildingData.locations.filter((_, i) => i !== idx)), [worldBuildingData.locations, onWorldBuildingDataChange]);
    const handleDeleteAllLocations = useCallback(() => { if (window.confirm('Delete all locations?')) onWorldBuildingDataChange('locations', []); }, [onWorldBuildingDataChange]);

    const handleAddCustomCategory = useCallback(() => onWorldBuildingDataChange('customCategories', [...worldBuildingData.customCategories, { id: `cat-${Date.now()}`, categoryName: '', items: [] }]), [worldBuildingData.customCategories, onWorldBuildingDataChange]);
    const handleCustomCategoryNameChange = useCallback((ci: number, name: string) => {
        const updated = worldBuildingData.customCategories.map((c, i) => (i === ci ? { ...c, categoryName: name } : c));
        onWorldBuildingDataChange('customCategories', updated);
    }, [worldBuildingData.customCategories, onWorldBuildingDataChange]);
    const handleDeleteCustomCategory = useCallback((ci: number) => onWorldBuildingDataChange('customCategories', worldBuildingData.customCategories.filter((_, i) => i !== ci)), [worldBuildingData.customCategories, onWorldBuildingDataChange]);
    const handleDeleteAllCustomCategories = useCallback(() => { if (window.confirm('Delete all categories?')) onWorldBuildingDataChange('customCategories', []); }, [onWorldBuildingDataChange]);

    const handleAddCustomItem = useCallback((ci: number) => {
        const updated = worldBuildingData.customCategories.map((c, i) =>
            i === ci ? { ...c, items: [...c.items, { id: `item-${Date.now()}`, name: '', description: '' }] } : c
        );
        onWorldBuildingDataChange('customCategories', updated);
    }, [worldBuildingData.customCategories, onWorldBuildingDataChange]);
    const handleCustomItemChange = useCallback((ci: number, ii: number, field: keyof CustomWorldBuildingCategoryItem, val: string) => {
        const updated = worldBuildingData.customCategories.map((c, i) =>
            i === ci
                ? { ...c, items: c.items.map((it, j) => (j === ii ? { ...it, [field]: val } : it)) }
                : c
        );
        onWorldBuildingDataChange('customCategories', updated);
    }, [worldBuildingData.customCategories, onWorldBuildingDataChange]);
    const handleDeleteCustomItem = useCallback((ci: number, ii: number) => {
        const updated = worldBuildingData.customCategories.map((c, i) =>
            i === ci ? { ...c, items: c.items.filter((_, j) => j !== ii) } : c
        );
        onWorldBuildingDataChange('customCategories', updated);
    }, [worldBuildingData.customCategories, onWorldBuildingDataChange]);

    // Extended character functions for CharactersView
    const handleCharacterNameChange = useCallback((index: number, value: string) => {
        const updated = worldBuildingData.characters.map((c, i) => (i === index ? { ...c, name: value } : c));
        onWorldBuildingDataChange('characters', updated);
    }, [worldBuildingData.characters, onWorldBuildingDataChange]);

    const handleCharacterDescriptionChange = useCallback((index: number, value: string) => {
        const updated = worldBuildingData.characters.map((c, i) => (i === index ? { ...c, description: value } : c));
        onWorldBuildingDataChange('characters', updated);
    }, [worldBuildingData.characters, onWorldBuildingDataChange]);

    const handleCharacterRoleChange = useCallback((index: number, value: string) => {
        const updated = worldBuildingData.characters.map((c, i) => (i === index ? { ...c, role: value } : c));
        onWorldBuildingDataChange('characters', updated);
    }, [worldBuildingData.characters, onWorldBuildingDataChange]);

    const handleCharacterBackstoryChange = useCallback((index: number, value: string) => {
        const updated = worldBuildingData.characters.map((c, i) => (i === index ? { ...c, backstory: value } : c));
        onWorldBuildingDataChange('characters', updated);
    }, [worldBuildingData.characters, onWorldBuildingDataChange]);

    const handleCharacterGoalsChange = useCallback((index: number, value: string) => {
        const updated = worldBuildingData.characters.map((c, i) => (i === index ? { ...c, goals: value } : c));
        onWorldBuildingDataChange('characters', updated);
    }, [worldBuildingData.characters, onWorldBuildingDataChange]);

    const handleCharacterConflictsChange = useCallback((index: number, value: string) => {
        const updated = worldBuildingData.characters.map((c, i) => (i === index ? { ...c, conflicts: value } : c));
        onWorldBuildingDataChange('characters', updated);
    }, [worldBuildingData.characters, onWorldBuildingDataChange]);

    const handleCharacterPersonalityChange = useCallback((index: number, value: string) => {
        const updated = worldBuildingData.characters.map((c, i) => (i === index ? { ...c, personality: value } : c));
        onWorldBuildingDataChange('characters', updated);
    }, [worldBuildingData.characters, onWorldBuildingDataChange]);

    const handleCharacterAppearanceChange = useCallback((index: number, value: string) => {
        const updated = worldBuildingData.characters.map((c, i) => (i === index ? { ...c, appearance: value } : c));
        onWorldBuildingDataChange('characters', updated);
    }, [worldBuildingData.characters, onWorldBuildingDataChange]);

    const handleCharacterRelationshipsChange = useCallback((index: number, value: string) => {
        const updated = worldBuildingData.characters.map((c, i) => (i === index ? { ...c, relationships: value } : c));
        onWorldBuildingDataChange('characters', updated);
    }, [worldBuildingData.characters, onWorldBuildingDataChange]);

    const handleCharacterDevelopmentChange = useCallback((index: number, value: string) => {
        const updated = worldBuildingData.characters.map((c, i) => (i === index ? { ...c, developmentArc: value } : c));
        onWorldBuildingDataChange('characters', updated);
    }, [worldBuildingData.characters, onWorldBuildingDataChange]);

    const handleCharacterNotesChange = useCallback((index: number, value: string) => {
        const updated = worldBuildingData.characters.map((c, i) => (i === index ? { ...c, notes: value } : c));
        onWorldBuildingDataChange('characters', updated);
    }, [worldBuildingData.characters, onWorldBuildingDataChange]);

    const handleRefineCharacters = useCallback(() => {
        const currentCharactersText = worldBuildingData.characters.map(c => `${c.name}: ${c.description || ''}`).join('\n');
        handleRefineCharactersWithAI(currentCharactersText);
    }, [worldBuildingData.characters, handleRefineCharactersWithAI]);

    const handleExpandCharacters = useCallback(() => {
        const currentCharactersText = worldBuildingData.characters.map(c => `${c.name}: ${c.description || ''}`).join('\n');
        // Use a specific expand prompt instead of refine
        if (!apiKey) { openApiKeyModal?.('apiKey'); return; }
        setLoadingState('parseCharacters', true);
        aiMutation.mutate({ task: 'world-building-characters-expand', payload: { currentCharacters: worldBuildingData.characters, newText: `Expand these characters with more detailed descriptions, backgrounds, and motivations:\n${currentCharactersText}`, outlineIdea, bookMetadata, coreConcepts } });
    }, [worldBuildingData.characters, outlineIdea, bookMetadata, coreConcepts, aiMutation, openApiKeyModal, setLoadingState, apiKey]);

    const isRefineCharactersLoading = aiMutation.isPending && aiMutation.variables?.task === 'world-building-characters';
    const isExpandCharactersLoading = aiMutation.isPending && aiMutation.variables?.task === 'world-building-characters-expand';

    return {
        worldBuildingData,
        handleRefineCharactersWithAI,
        handleRefineLocationsWithAI,
        handleGenerateCustomCategoryItemsWithAI,
        handleRefineCustomCategoryItemsWithAI,
        handleRegenerateAllWorldBuildingData,
        handleStoryTitleIdeasChange,
        handleSelectedStoryTitleChange,
        handleCharacterChange,
        handleAddCharacter,
        handleDeleteCharacter,
        handleDeleteAllCharacters,
        handleLocationChange,
        handleAddLocation,
        handleDeleteLocation,
        handleDeleteAllLocations,
        handleAddCustomCategory,
        handleCustomCategoryNameChange,
        handleDeleteCustomCategory,
        handleDeleteAllCustomCategories,
        handleAddCustomItem,
        handleCustomItemChange,
        handleDeleteCustomItem,
        
        // Extended character functions for CharactersView
        handleCharacterNameChange,
        handleCharacterDescriptionChange,
        handleCharacterRoleChange,
        handleCharacterBackstoryChange,
        handleCharacterGoalsChange,
        handleCharacterConflictsChange,
        handleCharacterPersonalityChange,
        handleCharacterAppearanceChange,
        handleCharacterRelationshipsChange,
        handleCharacterDevelopmentChange,
        handleCharacterNotesChange,
        handleRefineCharacters,
        handleExpandCharacters,
        isRefineCharactersLoading,
        isExpandCharactersLoading,
    };
};
